// Case Western Reserve University: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - CWRU's Common Data Set 2025-2026 ("CDS 2025-26 Adjusted Final.pdf"),
//    linked from Institutional Research's Common Data Set page. C1
//    applicants/admits/enrollees for Fall 2025 with the residency table; C8
//    test policy for Fall 2027; C21/C22 early plans. Quirk: the CDS checks
//    the box saying its 2026-2027 costs were not yet final (it gives tuition
//    only), so the cost table comes from the financial aid office instead.
//  - CWRU Undergraduate Admission: Deadlines and Requirements, Test Policy
//    and First-Year Applicants. The deadline table carries no year; it is the
//    table as posted on 2026-10-01 for the application now open (fall 2027
//    entry), and the CDS C8 block confirms the test-optional policy for fall
//    2027 applicants.
//  - CWRU Financial Aid, Estimated Costs of Attendance for 2026-27, the
//    first-year column (entering fall 2026). Transportation is listed as
//    "varies" and has no figure; the additional nursing fee applies to
//    nursing students only, so neither is in the total. The matriculation
//    fee is a first-year charge and is included.
//  - College Scorecard, school id 201645 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'CWRU Common Data Set 2025-2026',
        url: 'https://case.edu/ir/common-data-set-0',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'CWRU Undergraduate Admission, Deadlines and Requirements',
        url: 'https://case.edu/admission/apply/deadlines-and-requirements',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'CWRU Undergraduate Admission, First-Year Applicants',
        url: 'https://case.edu/admission/apply/first-year-applicants',
        read: '2026-10-01',
    },
    tests: {
        label: 'CWRU Undergraduate Admission, Test Policy',
        url: 'https://case.edu/admission/apply/deadlines-and-requirements/test-policy',
        read: '2026-10-01',
    },
    costs: {
        label: 'CWRU Financial Aid, Estimated Costs of Attendance for 2026-27',
        url: 'https://case.edu/financialaid/undergraduates/estimated-costs-attendance-2026-27',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?201645-Case-Western-Reserve-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'case-western-reserve-university',
    kbId: 'case_western_reserve_university',
    name: 'Case Western Reserve University',
    shortName: 'Case Western',
    aliases: ['Case'],
    location: 'Cleveland, OH',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (CWRU brand color guidelines); decoration only.
    branding: {
        primary: '#003071',
        secondary: '#09143A',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/case_western_reserve_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 39332,
        admitted: 13890,
        enrolled: 1663,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 3596, admitted: 972 },
            { group: 'Out-of-state', applied: 24624, admitted: 10558 },
            { group: 'International', applied: 11112, admitted: 2360 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'CWRU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision', applied: 1053, admitted: 263 },
        ],
        note: 'The Common Data Set reports early decision applicants and admits for the fall 2025 entering class; they are part of the totals above. CWRU also has a nonbinding early action plan, but the CDS gives no early action counts.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Everyone applies to CWRU through the Common App or Coalition with Scoir. A few applicants send more:',
        rows: [
            { school: 'Music and music education', requirement: 'An Arts Supplement is required for music and music education majors, and for anyone seeking an Arts Achievement Scholarship in music or art studio. It is optional for everyone else.', source: 'deadlines' },
            { school: 'Pre-Professional Scholars Program', requirement: 'Conditional admission to the School of Medicine or School of Dental Medicine. Applicants write two more essays, and finalists have a required interview in late March.', source: 'firstYear' },
        ],
        note: 'The Arts Supplement is due 15 days after your application deadline and is completed in your applicant portal.',
        source: 'deadlines',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'December 19', binding: false },
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'December 5', binding: true },
            { plan: 'Pre-Professional Scholars Program', deadline: 'December 1', decision: 'January 30', binding: false },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'February 6', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'March 20', binding: false },
        ],
        note: 'Decision dates are the ones CWRU calls anticipated. Financial aid documents are due November 15 for Early Action and Early Decision I, December 15 for the Pre-Professional Scholars Program, January 22 for Early Decision II and February 1 for Regular Decision. Admitted Early Decision I students enroll by December 12 and Early Decision II students within a week of admission; everyone else by May 1.',
        source: 'deadlines',
        binding: 'Early Decision I and II are binding: if CWRU admits you, you agree to withdraw all other college applications and enroll.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'CWRU is test-optional for every undergraduate program, the Pre-Professional Scholars Program and its scholarships. You choose when you apply whether to send SAT or ACT scores, and you can change your mind up to two weeks before your decision release date. You may self-report scores; enrolling students confirm them with official reports.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 71410 },
            { item: 'Fees', amount: 696 },
            { item: 'Matriculation fee', amount: 760 },
            { item: 'Housing (residential)', amount: 11688 },
            { item: 'Food (meal plan, unlimited meals)', amount: 8600 },
            { item: 'Books and supplies', amount: 1200 },
            { item: 'Personal expenses', amount: 1350 },
        ],
        total: 95704,
        note: 'For a first-year student living on campus. Transportation varies and has no figure, so it is not in the total. Nursing students pay an additional $1,450 in fees.',
        source: 'costs',
        netPrice: {
            average: 44622,
            byIncome: [
                { income: '$0 to $30,000', amount: 14046 },
                { income: '$30,001 to $48,000', amount: 17955 },
                { income: '$48,001 to $75,000', amount: 24534 },
                { income: '$75,001 to $110,000', amount: 31965 },
                { income: 'Over $110,000', amount: 55534 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from CWRU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
